import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, ExternalLink, Upload, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { API, BACKEND_URL } from "@/lib/site";

const empty = {
  title: "", location: "", property_type: "", price_from: "", price_to: "",
  bedrooms: "", bathrooms: "", built_up: "", tenure: "", status: "",
  description: "", features: [], published: true,
};

export default function ProjectManager({ adminKey }) {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [featureInput, setFeatureInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const headers = { "x-admin-key": adminKey };

  const load = useCallback(() => {
    axios.get(`${API}/projects`, { headers }).then((r) => setProjects(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditing({}); };
  const openEdit = (p) => {
    setForm({
      title: p.title || "", location: p.location || "", property_type: p.property_type || "",
      price_from: p.price_from || "", price_to: p.price_to || "",
      bedrooms: p.bedrooms || "", bathrooms: p.bathrooms || "", built_up: p.built_up || "",
      tenure: p.tenure || "", status: p.status || "",
      description: p.description || "", features: p.features || [], published: p.published,
    });
    setEditing(p);
  };
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addFeature = () => {
    const v = featureInput.trim();
    if (!v) return;
    update("features", [...form.features, v]);
    setFeatureInput("");
  };
  const removeFeature = (i) => update("features", form.features.filter((_, idx) => idx !== i));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Tajuk projek diperlukan"); return; }
    setSaving(true);
    try {
      if (editing.id) {
        const res = await axios.put(`${API}/projects/${editing.id}`, form, { headers });
        setEditing(res.data);
        toast.success("Projek dikemas kini");
      } else {
        const res = await axios.post(`${API}/projects`, form, { headers });
        setEditing(res.data);
        toast.success("Projek disimpan. Kini anda boleh muat naik gambar.");
      }
      load();
    } catch {
      toast.error("Gagal menyimpan projek");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Padam projek ini?")) return;
    try {
      await axios.delete(`${API}/projects/${id}`, { headers });
      toast.success("Projek dipadam");
      load();
    } catch { toast.error("Gagal memadam"); }
  };

  const onUpload = async (e) => {
    if (!editing?.id) { toast.error("Simpan projek dahulu sebelum muat naik gambar"); return; }
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await axios.post(`${API}/projects/${editing.id}/images`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setEditing(res.data);
      load();
      toast.success("Gambar dimuat naik");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal muat naik gambar");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = async (index) => {
    if (!window.confirm("Padam gambar ini?")) return;
    try {
      const res = await axios.delete(`${API}/projects/${editing.id}/images/${index}`, { headers });
      setEditing(res.data);
      load();
      toast.success("Gambar dipadam");
    } catch { toast.error("Gagal memadam gambar"); }
  };

  if (editing) {
    return (
      <div className="space-y-6" data-testid="project-editor">
        <div className="rounded-2xl bg-white border border-slate-200 p-7">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-bold text-[#0A2540]">
              {editing.id ? "Edit Projek" : "Projek Baru"}
            </h2>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700" data-testid="project-editor-close">
              <X className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={save} className="space-y-5">
            <div>
              <Label className="text-[#0A2540]">Tajuk Projek *</Label>
              <Input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="cth: Residensi Suria, Shah Alam" className="mt-1.5" data-testid="project-input-title" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-[#0A2540]">Lokasi</Label>
                <Input value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="cth: Shah Alam, Selangor" className="mt-1.5" data-testid="project-input-location" />
              </div>
              <div>
                <Label className="text-[#0A2540]">Jenis Hartanah</Label>
                <Input value={form.property_type} onChange={(e) => update("property_type", e.target.value)} placeholder="cth: Kondominium / Teres 2 Tingkat" className="mt-1.5" data-testid="project-input-type" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-[#0A2540]">Harga Dari (RM)</Label>
                <Input value={form.price_from} onChange={(e) => update("price_from", e.target.value)} placeholder="450,000" className="mt-1.5" data-testid="project-input-price-from" />
              </div>
              <div>
                <Label className="text-[#0A2540]">Harga Hingga (pilihan)</Label>
                <Input value={form.price_to} onChange={(e) => update("price_to", e.target.value)} placeholder="750,000" className="mt-1.5" data-testid="project-input-price-to" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div>
                <Label className="text-[#0A2540]">Bilik Tidur</Label>
                <Input value={form.bedrooms} onChange={(e) => update("bedrooms", e.target.value)} placeholder="cth: 3 - 4" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-[#0A2540]">Bilik Air</Label>
                <Input value={form.bathrooms} onChange={(e) => update("bathrooms", e.target.value)} placeholder="cth: 2" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-[#0A2540]">Keluasan</Label>
                <Input value={form.built_up} onChange={(e) => update("built_up", e.target.value)} placeholder="cth: 1,200 sqft" className="mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-[#0A2540]">Milikan (Tenure)</Label>
                <Input value={form.tenure} onChange={(e) => update("tenure", e.target.value)} placeholder="Freehold / Leasehold" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-[#0A2540]">Status</Label>
                <Input value={form.status} onChange={(e) => update("status", e.target.value)} placeholder="Baru Dilancar / Sedang Dibina / Siap" className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="text-[#0A2540]">Butiran Projek (Markdown)</Label>
              <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={8} placeholder="Ceritakan tentang projek ini... Guna ## untuk tajuk, - untuk senarai." className="mt-1.5 font-mono text-sm" data-testid="project-input-description" />
            </div>
            <div>
              <Label className="text-[#0A2540]">Kemudahan / Ciri-ciri</Label>
              <div className="mt-1.5 flex gap-2">
                <Input value={featureInput} onChange={(e) => setFeatureInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }}
                  placeholder="cth: Kolam renang, Gimnasium..." data-testid="project-input-feature" />
                <Button type="button" onClick={addFeature} className="bg-[#0A2540] hover:bg-[#0d3358] text-white rounded-full">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {form.features.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {form.features.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">
                      {f}
                      <button type="button" onClick={() => removeFeature(i)} className="text-slate-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.published} onCheckedChange={(v) => update("published", v)} data-testid="project-input-published" />
              <span className="text-sm text-slate-600">{form.published ? "Diterbitkan" : "Draf"}</span>
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={saving} className="bg-[#0A2540] hover:bg-[#0d3358] text-white rounded-full" data-testid="project-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing.id ? "Kemas Kini Projek" : "Simpan Projek"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-full">Selesai</Button>
            </div>
          </form>
        </div>

        {editing.id && (
          <div className="rounded-2xl bg-white border border-slate-200 p-7">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading font-bold text-[#0A2540]">Galeri Gambar</h3>
                <p className="text-xs text-slate-500">Muat naik gambar projek (JPG/PNG, maks 6MB setiap fail)</p>
              </div>
              <label className="cursor-pointer" data-testid="project-upload-label">
                <input type="file" multiple accept=".jpg,.jpeg,.png,.webp" onChange={onUpload} className="hidden" data-testid="project-file-input" />
                <span className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] px-5 py-2 text-sm font-semibold text-white hover:bg-[#0d3358] transition-colors">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Muat Naik
                </span>
              </label>
            </div>
            {editing.images?.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {editing.images.map((_, i) => (
                  <div key={i} className="relative rounded-xl overflow-hidden border border-slate-200 aspect-square" data-testid={`project-image-${i}`}>
                    <img src={`${BACKEND_URL}/api/projects/${editing.id}/images/${i}`} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => removeImage(i)} data-testid={`project-remove-image-${i}`}
                      className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 text-red-500 hover:bg-white">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-300 py-10 text-center text-slate-400 text-sm">
                <ImageIcon className="mx-auto h-8 w-8" />
                <p className="mt-2">Belum ada gambar. Muat naik untuk mula.</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="project-manager">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-lg font-bold text-[#0A2540]">Urus Projek Perumahan</h2>
        <Button onClick={openNew} className="bg-[#0A2540] hover:bg-[#0d3358] text-white rounded-full" data-testid="project-new-btn">
          <Plus className="mr-1.5 h-4 w-4" /> Projek Baru
        </Button>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        {projects.length === 0 ? (
          <p className="py-16 text-center text-slate-500" data-testid="project-manager-empty">Belum ada projek. Klik &quot;Projek Baru&quot; untuk mula.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {projects.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-6 py-4" data-testid={`project-row-${p.slug}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <div className="h-12 w-16 shrink-0 rounded-lg bg-slate-100 overflow-hidden">
                    {p.images?.length > 0 && <img src={`${BACKEND_URL}/api/projects/${p.id}/images/0`} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#0A2540] truncate">{p.title}</p>
                    <p className="text-xs text-slate-500 truncate">{p.location || "—"} • {p.images?.length || 0} gambar</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge className={p.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                    {p.published ? "Terbit" : "Draf"}
                  </Badge>
                  <a href={`/projek/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#1D4ED8]"><ExternalLink className="h-4 w-4" /></a>
                  <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-[#0A2540]" data-testid={`project-edit-${p.slug}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-500" data-testid={`project-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
